import { useQuery } from "@tanstack/react-query";
import { Link, useParams } from "wouter";
import { useState, useEffect, useMemo } from "react";
import { Header } from "@/components/layout/header";
import { Footer } from "@/components/layout/footer";
import { SkeletonText } from "@/components/ui/luxury-skeleton";
import { useSEO, useSEOSettings, generateBreadcrumbSchema, generateSpeakableSchema } from "@/lib/seo-utils";
import { ChevronLeft, ArrowRight } from "lucide-react";
import type { BlogPostWithCategory } from "@shared/schema";
import { HomesPicker } from "@/components/property/homes-picker";
import { Breadcrumbs } from "@/components/layout/breadcrumbs";
import { BlogStayCta } from "@/components/blog/blog-stay-cta";
import { ArticleMasthead } from "@/components/blog/article-masthead";
import { readingMinutes } from "@/components/blog/reading-time";
import { cleanExcerpt } from "@/components/blog/excerpt";
import { StickyMobileBookCta } from "@/components/layout/sticky-mobile-book-cta";
import { optimizedUrl, optimizedSrcSet } from "@/lib/img";
import { useBookCta } from "@/hooks/use-book-href";

function extractHeadings(html: string): { id: string; text: string; level: number }[] {
  const headings: { id: string; text: string; level: number }[] = [];
  const headingRegex = /<h([23])[^>]*>([\s\S]*?)<\/h[23]>/gi;
  let match;
  
  while ((match = headingRegex.exec(html)) !== null) {
    const level = parseInt(match[1]);
    const rawText = match[2].replace(/<[^>]*>/g, '').trim();
    if (rawText.length > 0 && rawText.length < 100) {
      const id = rawText.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
      headings.push({ id, text: rawText, level });
    }
  }
  
  return headings;
}

function addHeadingIds(html: string): string {
  return html.replace(/<h([23])([^>]*)>([\s\S]*?)<\/h[23]>/gi, (match, level, attrs, content) => {
    const text = content.replace(/<[^>]*>/g, '').trim();
    const id = text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
    return `<h${level}${attrs} id="${id}">${content}</h${level}>`;
  });
}

/**
 * Build FAQPage entries from question-shaped headings already present in a post.
 *
 * Google requires FAQ structured data to match content visible on the page, so
 * nothing here is generated: the question is the heading as rendered, and the
 * answer is the prose that already follows it. Posts whose headings are not
 * questions simply produce no FAQ block rather than having questions invented
 * for them - marking up Q&A a reader cannot see is the structured-data problem
 * this site already had once with its review count.
 *
 * Only emitted at two or more pairs; a lone question is not an FAQ.
 */
function extractFaqPairs(html: string): { q: string; a: string }[] {
  const pairs: { q: string; a: string }[] = [];
  const strip = (x: string) => x.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
  const re = /<h([23])[^>]*>([\s\S]*?)<\/h\1>([\s\S]*?)(?=<h[23][^>]*>|$)/gi;
  let m: RegExpExecArray | null;
  while ((m = re.exec(html)) !== null) {
    const q = strip(m[2]);
    if (!q.endsWith("?")) continue;
    const a = strip(m[3]);
    if (a.length < 80) continue;
    pairs.push({ q, a: a.length > 1200 ? a.slice(0, 1200).replace(/\s+\S*$/, "") + "..." : a });
  }
  return pairs;
}

/**
 * Strip the document scaffolding that 72 of the 113 posts carry.
 *
 * Those rows store a complete `<!DOCTYPE html>` document - head, title, meta
 * and all - in the content column. The browser's fragment parser drops
 * html/head/body and hides title/meta, so nothing was visibly wrong, but the
 * markup still reached the page: a second <title> and a duplicate
 * <meta name="description"> rendered inside <body>, competing with the ones
 * useSEO sets in <head>. A nested <main> is invalid outright.
 *
 * Cleaning here fixes every affected post without rewriting 72 database rows.
 */
function stripDocumentWrapper(html: string): string {
  return html
    .replace(/<!DOCTYPE[^>]*>/gi, "")
    .replace(/<head[\s\S]*?<\/head>/gi, "")
    .replace(/<\/?(?:html|body|main)(?:\s[^>]*)?>/gi, "")
    .replace(/<title[\s\S]*?<\/title>/gi, "")
    .replace(/<meta(?:\s[^>]*)?\/?>/gi, "")
    .trim();
}

export default function BlogPostPage() {
  const { slug } = useParams<{ slug: string }>();
  const { seoSettings } = useSEOSettings();
  const bookCta = useBookCta();
  const [activeSection, setActiveSection] = useState<string>('');

  const { data: post, isLoading, error } = useQuery<BlogPostWithCategory>({
    queryKey: ['/api/blog/posts', slug],
    queryFn: async () => {
      const response = await fetch(`/api/blog/posts/${slug}`);
      if (!response.ok) {
        if (response.status === 404) throw new Error('Post not found');
        throw new Error('Failed to fetch post');
      }
      return response.json();
    },
    enabled: !!slug,
  });

  const { data: relatedPosts } = useQuery<BlogPostWithCategory[]>({
    queryKey: ['/api/blog/posts', slug, 'related'],
    queryFn: async () => {
      const response = await fetch(`/api/blog/posts/${slug}/related`);
      if (!response.ok) throw new Error('Failed to fetch related posts');
      return response.json();
    },
    enabled: !!slug && !!post,
  });

  const cleanedContent = useMemo(() => (post ? stripDocumentWrapper(post.content) : ''), [post]);
  const headings = useMemo(() => extractHeadings(cleanedContent), [cleanedContent]);
  const processedContent = useMemo(() => addHeadingIds(cleanedContent), [cleanedContent]);
  const faqPairs = useMemo(() => extractFaqPairs(cleanedContent), [cleanedContent]);

  useEffect(() => {
    if (!headings.length) return;
    
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
          }
        });
      },
      { rootMargin: '-100px 0px -66%' }
    );

    headings.forEach(({ id }) => {
      const element = document.getElementById(id);
      if (element) observer.observe(element);
    });

    return () => observer.disconnect();
  }, [headings]);

  const categoryKeywords: Record<string, string> = {
    "beaches-coastal-guides": "oahu beaches, best beaches hawaii, beach guide oahu",
    "snorkeling-water-activities": "snorkeling oahu, water activities hawaii, oahu surf spots",
    "hiking-outdoor-adventure": "oahu hiking, hawaii trails, outdoor adventures oahu",
    "food-local-culture": "hawaiian food, local restaurants oahu, saturday farmers market oahu",
    "trip-planning-itineraries": "oahu itinerary, hawaii vacation planning, oahu trip guide",
    "transportation-practical-guides": "oahu transportation, getting around hawaii, car rental oahu",
  };

  const categorySlug = post?.category?.slug || '';
  const additionalKeywords = categoryKeywords[categorySlug] || '';

  const seoHead = useSEO({
    pageType: "article",
    title: post?.meta_title || post?.title,
    description: post?.meta_description || cleanExcerpt(post?.title, post?.excerpt, 300),
    keywords: post?.category?.name 
      ? `${post.category.name}, Oahu, Hawaii travel, ${additionalKeywords}, vacation rental oahu, oahu travel guide` 
      : undefined,
    ogType: "article",
    ogTitle: post?.meta_title || post?.title,
    ogDescription: post?.meta_description || cleanExcerpt(post?.title, post?.excerpt, 300),
    canonical: post?.slug ? `https://vacationrentaloahu.co/blog/${post.slug}` : undefined,
    twitterCard: "summary_large_image",
    structuredData: seoSettings && post ? [
      generateBreadcrumbSchema([
        { name: "Home", url: "/" },
        { name: "Travel Guide", url: "/blog" },
        { name: post.category.name, url: `/blog/category/${post.category.slug}` },
        { name: post.title, url: `/blog/${post.slug}` }
      ]),
      {
        "@context": "https://schema.org",
        "@type": "Article",
        "headline": post.title,
        "alternativeHeadline": `${post.category.name}: ${post.title.split(':')[0] || post.title}`,
        "description": post.excerpt,
        ...(post.hero_image_url && { "image": post.hero_image_url }),
        "articleBody": cleanedContent.replace(/<[^>]*>/g, "").substring(0, 500) + '...',
        "wordCount": post.word_count || 0,
        "datePublished": post.published_at?.toString() || post.created_at.toString(),
        "dateModified": post.updated_at.toString(),
        "author": {
          "@type": "Organization",
          "@id": "https://vacationrentaloahu.co/#organization",
          "name": "The Kualoa Beach House",
          "url": "https://vacationrentaloahu.co"
        },
        "publisher": {
          "@type": "Organization",
          "@id": "https://vacationrentaloahu.co/#organization",
          "name": "The Kualoa Beach House",
          "url": "https://vacationrentaloahu.co",
          "logo": {
            "@type": "ImageObject",
            "url": "https://vacationrentaloahu.co/logo.png"
          }
        },
        "mainEntityOfPage": {
          "@type": "WebPage",
          "@id": `https://vacationrentaloahu.co/blog/${post.slug}`
        },
        "articleSection": post.category.name,
        "keywords": `${post.category.name}, Oahu, Hawaii, ${additionalKeywords}`,
        "about": {
          "@type": "Place",
          "name": "Oahu, Hawaii",
          "address": {
            "@type": "PostalAddress",
            "addressLocality": "Oahu",
            "addressRegion": "HI",
            "addressCountry": "US"
          }
        },
        "inLanguage": "en-US",
        "isAccessibleForFree": true,
        "isPartOf": {
          "@type": "Blog",
          "name": "Oahu Travel Guide",
          "url": "https://vacationrentaloahu.co/blog"
        }
      },
      generateSpeakableSchema(["h1", ".article-deck", ".faq-answer"]),
      ...(faqPairs.length >= 2 ? [{
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": faqPairs.map(({ q, a }) => ({
          "@type": "Question",
          "name": q,
          "acceptedAnswer": { "@type": "Answer", "text": a },
        })),
      }] : []),
    ] : []
  });

  if (isLoading) {
    return (
      <div className="min-h-screen bg-shell pb-24 lg:pb-0">
        <Header />
        <main className="pt-32 pb-16">
          <div className="container mx-auto max-w-3xl px-6 text-center">
            <SkeletonText lines={1} className="mx-auto mb-6 h-3 w-40" />
            <SkeletonText lines={1} className="mx-auto mb-6 h-12 w-3/4" />
            <SkeletonText lines={2} className="mx-auto w-2/3" />
          </div>
          <div className="container mx-auto mt-16 grid max-w-6xl gap-12 px-6 lg:grid-cols-[minmax(0,1fr)_340px]">
            <SkeletonText lines={20} />
            <SkeletonText lines={8} />
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="min-h-screen bg-shell pb-24 lg:pb-0">
        <Header />
        <main className="pt-32 pb-16">
          <div className="container mx-auto max-w-3xl px-6 py-20 text-center">
            <h1 className="mb-4 font-serif text-luxury-2xl font-normal tracking-tight text-forest">
              Article Not Found
            </h1>
            <p className="mb-10 font-sans text-[15px] font-light leading-relaxed text-ink-soft">
              The article you're looking for doesn't exist or has been moved.
            </p>
            <Link
              href="/blog"
              className="inline-flex min-h-[24px] items-center gap-2 border-b border-sand-deep pb-1 font-sans text-[11px] uppercase tracking-[0.24em] text-forest transition-colors hover:border-forest"
              data-testid="link-back-to-blog"
            >
              <ChevronLeft className="h-3.5 w-3.5" />
              Back to Travel Guide
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const postMinutes = readingMinutes(post);
  const deck = cleanExcerpt(post.title, post.excerpt, 260);
  const publishedLabel = post.published_at
    ? new Date(post.published_at).toLocaleDateString("en-US", {
        month: "long",
        day: "numeric",
        year: "numeric",
      })
    : "";

  return (
    <>
    {seoHead}
    <div className="min-h-screen bg-shell pb-24 lg:pb-0">
      <Header />

      <main id="main-content">
        {/* The masthead is the fix for the blog's biggest break with the rest of
            the site: every other page opens with a hero, and articles opened
            with a breadcrumb and a pill badge. */}
        <ArticleMasthead
          eyebrow={post.category.name}
          eyebrowHref={`/blog/category/${post.category.slug}`}
          title={post.title}
          deck={deck}
          meta={[publishedLabel, postMinutes !== null ? `${postMinutes} min read` : ""]}
        />

        <article className="container mx-auto max-w-6xl px-6">
          <Breadcrumbs
            items={[
              { name: "Home", url: "/" },
              { name: "Travel Guide", url: "/blog" },
              { name: post.category.name, url: `/blog/category/${post.category.slug}` },
              { name: post.title, url: `/blog/${post.slug}` },
            ]}
            className="py-8"
          />

          {post.hero_image_url && (
            <figure className="mb-14">
              <img
                src={optimizedUrl(post.hero_image_url, 1200)}
                srcSet={optimizedSrcSet(post.hero_image_url, [480, 768, 960, 1200, 1600])}
                sizes="(max-width: 1152px) 100vw, 1152px"
                alt={post.hero_image_alt || `${post.title} | Oahu travel guide`}
                className="max-h-[460px] w-full object-cover"
                loading="eager"
                decoding="async"
                fetchPriority="high"
              />
            </figure>
          )}

          <div className="grid grid-cols-[minmax(0,1fr)] gap-14 pb-6 lg:grid-cols-[minmax(0,1fr)_340px] lg:gap-16">
            {/* Main column */}
            <div className="min-w-0">
              <BlogStayCta className="mb-12" categorySlug={categorySlug} postSlug={post.slug} />

              {/* On mobile the sidebar lands after ~18,000px of article, which
                  puts the contents where nobody can use them. */}
              {headings.length > 0 && (
                <details className="group mb-12 border border-sand-deep bg-sand/50 lg:hidden" data-testid="section-table-of-contents-mobile">
                  <summary className="flex cursor-pointer list-none items-center justify-between gap-4 px-6 py-4 font-sans text-[10px] uppercase tracking-[0.3em] text-driftwood marker:content-none [&::-webkit-details-marker]:hidden">
                    In this article
                    <ChevronLeft className="h-3.5 w-3.5 shrink-0 -rotate-90 transition-transform duration-300 group-open:rotate-90" aria-hidden="true" />
                  </summary>
                  <ul className="space-y-1 border-t border-sand-deep px-6 py-4">
                    {headings.slice(0, 12).map(({ id, text, level }) => (
                      <li key={id}>
                        <a
                          href={`#${id}`}
                          className={`block py-1.5 font-sans text-[14px] leading-snug text-ink-soft ${level === 3 ? "pl-4" : ""}`}
                        >
                          {text}
                        </a>
                      </li>
                    ))}
                  </ul>
                </details>
              )}

              <div
                className="prose prose-lg max-w-none
                  prose-headings:font-serif prose-headings:font-normal prose-headings:tracking-tight prose-headings:text-forest prose-headings:scroll-mt-32
                  prose-h2:text-[clamp(22px,3vw,30px)] prose-h2:leading-snug prose-h2:mt-14 prose-h2:mb-5 prose-h2:pb-3 prose-h2:border-b prose-h2:border-sand-deep
                  prose-h3:text-[clamp(18px,2.4vw,22px)] prose-h3:mt-10 prose-h3:mb-3
                  prose-p:font-sans prose-p:font-light prose-p:text-ink-soft prose-p:leading-[1.8] prose-p:mb-5
                  prose-a:text-forest prose-a:font-normal prose-a:no-underline
                  prose-strong:font-normal prose-strong:text-ink
                  prose-ul:my-6 prose-ol:my-6
                  prose-li:font-sans prose-li:font-light prose-li:text-ink-soft prose-li:leading-[1.8] prose-li:mb-2
                  prose-blockquote:border-l-2 prose-blockquote:border-driftwood prose-blockquote:pl-6 prose-blockquote:not-italic prose-blockquote:font-serif prose-blockquote:text-ink
                  prose-img:w-full prose-img:my-10
                  prose-hr:border-sand-deep prose-hr:my-12"
                dangerouslySetInnerHTML={{ __html: processedContent }}
                data-testid="text-post-content"
              />

              <div className="mt-14 border-t border-sand-deep pt-8">
                <Link
                  href="/blog"
                  className="inline-flex min-h-[24px] items-center gap-2 font-sans text-[11px] uppercase tracking-[0.24em] text-driftwood transition-colors hover:text-forest"
                  data-testid="link-back-to-blog-bottom"
                >
                  <ChevronLeft className="h-3.5 w-3.5" />
                  Back to Travel Guide
                </Link>
              </div>
            </div>

            {/* Sidebar. The table of contents leads because it is the reading
                aid; the booking widget follows it rather than opening the rail. */}
            <aside className="space-y-10 lg:sticky lg:top-28 lg:h-fit">
              {headings.length > 0 && (
                <nav
                  className="hidden border border-sand-deep bg-sand/50 p-6 lg:block"
                  aria-label="In this article"
                  data-testid="section-table-of-contents"
                >
                  <h3 className="mb-5 font-sans text-[10px] uppercase tracking-[0.3em] text-driftwood">
                    In This Article
                  </h3>
                  <ul className="space-y-1">
                    {headings.slice(0, 8).map(({ id, text, level }) => (
                      <li key={id}>
                        <a
                          href={`#${id}`}
                          className={`block border-l py-1.5 pl-3 font-sans text-[14px] leading-snug transition-colors ${
                            level === 3 ? "ml-3" : ""
                          } ${
                            activeSection === id
                              ? "border-driftwood text-forest"
                              : "border-transparent text-ink-soft hover:border-sand-deep hover:text-forest"
                          }`}
                          data-testid={`link-toc-${id}`}
                        >
                          {text}
                        </a>
                      </li>
                    ))}
                  </ul>
                </nav>
              )}

              <div className="border border-sand-deep bg-shell p-6" data-testid="section-booking">
                <h3 className="mb-5 font-sans text-[10px] uppercase tracking-[0.3em] text-driftwood">
                  Book Your Stay
                </h3>
                <HomesPicker headingLevel={3} />
              </div>

              {Array.isArray((post as any)?.nearby) && (post as any).nearby.length > 0 && (
                <div className="border border-sand-deep bg-sand/50 p-6" data-testid="section-places">
                  <h3 className="mb-5 font-sans text-[10px] uppercase tracking-[0.3em] text-driftwood">
                    Places in this guide
                  </h3>
                  <ul className="divide-y divide-sand-deep">
                    {((post as any).nearby as Array<{ name: string; slug: string; distance: string | null }>).map((place) => (
                      <li key={place.slug} className="py-3 first:pt-0 last:pb-0">
                        <Link href={`/nearby/${place.slug}`} className="group block" data-testid={`link-place-${place.slug}`}>
                          <span className="block font-serif text-[16px] leading-snug text-forest transition-colors group-hover:text-ink">{place.name}</span>
                          {place.distance && (
                            <span className="mt-1 block font-sans text-[10px] uppercase tracking-[0.24em] text-driftwood">{place.distance} from the homes</span>
                          )}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {relatedPosts && relatedPosts.length > 0 && (
                <div className="border border-sand-deep bg-sand/50 p-6" data-testid="section-related-guides">
                  <h3 className="mb-5 font-sans text-[10px] uppercase tracking-[0.3em] text-driftwood">
                    Related Guides
                  </h3>
                  <ul className="divide-y divide-sand-deep">
                    {relatedPosts.slice(0, 3).map((relatedPost) => (
                      <li key={relatedPost.id} className="py-3 first:pt-0 last:pb-0">
                        <Link
                          href={`/blog/${relatedPost.slug}`}
                          className="group block"
                          data-testid={`link-related-${relatedPost.slug}`}
                        >
                          <span className="block font-serif text-[16px] leading-snug text-forest transition-colors group-hover:text-ink">
                            {relatedPost.title}
                          </span>
                          {readingMinutes(relatedPost) !== null && (
                            <span className="mt-1 block font-sans text-[10px] uppercase tracking-[0.24em] text-driftwood">
                              {readingMinutes(relatedPost)} min
                            </span>
                          )}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="border border-sand-deep bg-sand/50 p-6" data-testid="section-quick-actions">
                <h3 className="mb-5 font-sans text-[10px] uppercase tracking-[0.3em] text-driftwood">
                  Keep Exploring
                </h3>
                <ul className="divide-y divide-sand-deep">
                  <li className="py-3 first:pt-0">
                    <Link
                      href={`/blog/category/${post.category.slug}`}
                      className="group flex items-center justify-between gap-3 font-sans text-[14px] text-ink-soft transition-colors hover:text-forest"
                      data-testid="link-browse-category"
                    >
                      More {post.category.name}
                      <ArrowRight className="h-3.5 w-3.5 shrink-0 text-driftwood transition-transform duration-300 group-hover:translate-x-1" />
                    </Link>
                  </li>
                  <li className="py-3 last:pb-0">
                    <Link
                      href="/kaaawa"
                      className="group flex items-center justify-between gap-3 font-sans text-[14px] text-ink-soft transition-colors hover:text-forest"
                      data-testid="link-view-location"
                    >
                      Where we are
                      <ArrowRight className="h-3.5 w-3.5 shrink-0 text-driftwood transition-transform duration-300 group-hover:translate-x-1" />
                    </Link>
                  </li>
                </ul>
              </div>
            </aside>
          </div>

          {/* Closing CTA */}
          <section className="mt-16 border-t border-sand-deep py-16 text-center md:py-20" data-testid="section-cta">
            <span className="mb-5 inline-block font-sans text-[11px] uppercase tracking-[0.3em] text-driftwood">
              Ka'a'awa &middot; Windward Oahu
            </span>
            <h2 className="mx-auto mb-5 max-w-[18ch] font-serif text-[clamp(26px,4vw,42px)] font-normal leading-tight tracking-tight text-forest">
              Ready for your Oahu adventure?
            </h2>
            <p className="mx-auto mb-10 max-w-[52ch] font-sans text-[16px] font-light leading-relaxed text-ink-soft">
              Two oceanfront homes in Ka'a'awa on the windward coast, a short drive from everything
              this guide covers. Book direct with no platform fees.
            </p>
            <div className="flex flex-col items-center justify-center gap-4 sm:flex-row">
              <Link
                href={bookCta.href}
                data-primary-book-cta
                className="inline-flex items-center justify-center bg-sun px-8 py-4 font-sans tracking-wide text-shell transition-colors duration-300 hover:bg-sun-hover"
                data-testid="button-cta-booking"
              >
                Check availability
              </Link>
              <Link
                href="/properties"
                className="inline-flex min-h-[24px] items-center gap-2 border-b border-sand-deep pb-1 font-sans text-[11px] uppercase tracking-[0.24em] text-forest transition-colors hover:border-forest"
                data-testid="link-cta-stay"
              >
                See our homes
                <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            </div>
          </section>
        </article>
      </main>

      <StickyMobileBookCta />
      <Footer />
    </div>
    </>
  );
}
